import { useObserva } from "@morganisbatman/observa-client-react";
import { useLocalSearchParams, useRouter } from "expo-router";
import { useEffect, useMemo, useState } from "react";
import {
  ActivityIndicator,
  Pressable,
  StyleSheet,
  Text,
  View,
} from "react-native";

import { fontFamily, space, textStyle } from "@passfirst/ui";

import { BrandSplit } from "../src/brand-split";
import { PinnedCtaFooter } from "../src/components/pinned-cta-footer";
import { StateChip } from "../src/state/state-chip";
import { useJurisdictions } from "../src/state/use-jurisdictions";
import { type Colors, useColors } from "../src/theme";

// Step 2 of onboarding — state picker for state-bound verticals.
// Loads jurisdictions via useJurisdictions, renders a 2-col grid of
// StateChips with California pre-selected. Emits
// onboarding.state_picked on continue.
export default function StatePicker(): JSX.Element {
  const router = useRouter();
  const observa = useObserva();
  const colors = useColors();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const params = useLocalSearchParams<{ vertical?: string }>();
  const verticalSlug =
    typeof params.vertical === "string" ? params.vertical : null;

  const data = useJurisdictions(verticalSlug);
  const [selected, setSelected] = useState<string | null>(null);

  useEffect(() => {
    if (data.defaultSelected && selected === null) {
      setSelected(data.defaultSelected);
    }
  }, [data.defaultSelected, selected]);

  function handleContinue(): void {
    if (!selected || !verticalSlug) return;
    void observa.event({
      eventType: "onboarding.state_picked",
      payload: { verticalSlug, jurisdictionCode: selected },
    });
    router.push({
      pathname: "/practice",
      params: { vertical: verticalSlug, jurisdiction: selected },
    });
  }

  if (data.error) {
    return (
      <View style={styles.errorContainer}>
        <Text style={styles.error}>{data.error}</Text>
        <Pressable onPress={() => router.back()} style={styles.ghost}>
          <Text style={styles.ghostText}>Back</Text>
        </Pressable>
      </View>
    );
  }
  if (!data.jurisdictions) {
    return (
      <View style={styles.errorContainer}>
        <ActivityIndicator color={colors.accentSoftText} />
      </View>
    );
  }

  // Capture the narrowed value — TS loses the null-guard inside the
  // BrandSplit render-prop closure otherwise.
  const jurisdictions = data.jurisdictions;

  return (
    <BrandSplit
      panelTitle="Set up in two questions."
      panelBody="Tell us your credential and we'll scope every question, the tutor, and the voice coach to exactly your exam."
      step={2}
      totalSteps={2}
      shieldLine="No account needed to start."
      contentWidth={460}
      onBack={() => router.back()}
      footer={
        <PinnedCtaFooter
          label="Start practicing →"
          onPress={handleContinue}
          disabled={!selected}
          accessibilityLabel="Start practicing"
          contentWidth={460}
        />
      }
    >
      {(isWide) => (
        <>
          <Text style={styles.eyebrow}>
            {isWide ? "STEP 2 OF 2" : "ONE MORE THING"}
          </Text>
          {isWide ? (
            <Text style={styles.titleWide}>Which state&apos;s exam?</Text>
          ) : (
            <Text style={styles.title}>Which state&apos;s{"\n"}exam?</Text>
          )}
          <Text style={styles.lede}>
            {data.verticalShortName ? `${data.verticalShortName} rules` : "Rules"}{" "}
            vary by state. We&apos;ll pull from that handbook plus the federal
            sections.
          </Text>

          <View style={styles.grid}>
            {jurisdictions.map((j) => (
              <StateChip
                key={j.code}
                label={j.name}
                selected={selected === j.code}
                onPress={() => setSelected(j.code)}
              />
            ))}
          </View>
        </>
      )}
    </BrandSplit>
  );
}

function makeStyles(colors: Colors) {
  return StyleSheet.create({
    errorContainer: {
      flex: 1,
      backgroundColor: colors.background,
      alignItems: "center",
      justifyContent: "center",
      padding: space[6],
      gap: space[4],
    },
    eyebrow: {
      ...textStyle.eyebrow,
      color: colors.action,
      marginBottom: space[3],
    },
    title: {
      fontFamily: fontFamily.extraBold,
      fontSize: 32,
      lineHeight: 36,
      letterSpacing: -0.5,
      color: colors.heading,
      marginBottom: space[3],
    },
    titleWide: {
      fontFamily: fontFamily.extraBold,
      fontSize: 22,
      lineHeight: 28,
      letterSpacing: -0.4,
      color: colors.heading,
      marginBottom: space[3],
    },
    lede: {
      ...textStyle.body,
      color: colors.textSecondary,
      marginBottom: space[6],
    },
    grid: { flexDirection: "row", flexWrap: "wrap", gap: 9 },
    ghost: { paddingVertical: space[3], paddingHorizontal: space[5] },
    ghostText: { ...textStyle.body, color: colors.textSecondary },
    error: {
      ...textStyle.body,
      color: colors.error,
      textAlign: "center",
    },
  });
}
